import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import { ColumnsContainer, DetailsColumn, MainColumn } from 'components/containers/ItemCampaignContainers';
import AddressSelection from 'features/checkout/delivery/AddressSelection';
import DeliveryMethod from 'features/checkout/delivery/DeliveryMethod';
import LocalPickupInfo from 'features/checkout/delivery/LocalPickupDetails';
import PaymentSelection from 'features/checkout/payment/PaymentSelection';
import PriceAndFeeSelection from 'features/checkout/price/PriceAndFeeSelection';
import OrderSummary from 'features/checkout/summary/OrderSummary';
import { usePaymentConfig } from 'providers/cache/cachedDataHooks';
import { ItemDto } from 'services/item/itemService.dto';
import { PurchaseDto } from 'services/order/orderService.dto';
import { TransactionDto } from 'services/payment/paymentService.dto';

import ItemDetails from './ItemDetails';
import ItemHeader from './ItemHeader';
import { useItemCheckout } from './useItemCheckout';

const ContentSeparator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${theme.color.darkGray};
  margin: 24px 0;
`;

const CheckoutWrapper = styled.div`
  padding-bottom: 100px;
`;

interface Props {
  item: ItemDto;
  payForOffer: boolean;
  onPurchasePaid: (purchase: PurchaseDto, transaction: TransactionDto) => void;
  existingOrder?: PurchaseDto;
  isInitializing?: boolean;
  canAdjustPrice?: boolean;
}

const ItemCheckoutContent = ({
  item,
  payForOffer,
  onPurchasePaid,
  existingOrder,
  isInitializing = false,
  canAdjustPrice = false,
}: Props) => {
  const {
    deliveryType,
    setDeliveryType,
    isDeliveryTypeLocked,
    purchase,
    isProcessingPurchase,
    isProcessingPayment,
    checkoutCardStatus,
    checkoutTokenStatus,
    confirmCheckoutWithCard,
    confirmCheckoutWithToken,
    setFee,
    setPrice,
  } = useItemCheckout(item, payForOffer, onPurchasePaid, existingOrder, isInitializing);

  const { paymentConfig } = usePaymentConfig();
  const displayPaymentSelection = paymentConfig?.availablePaymentMethodTypes.includes('creditCard');

  return (
    <CheckoutWrapper>
      <ColumnsContainer data-testid={'item-checkout'}>
        <MainColumn>
          <ItemHeader item={item} />
          <ItemDetails item={item} />
          {canAdjustPrice && (
            <PriceAndFeeSelection
              purchase={purchase}
              onFeeChange={setFee}
              onPriceChange={setPrice}
              isProcessing={isProcessingPayment || isProcessingPurchase || isInitializing}
            />
          )}
          {canAdjustPrice && <ContentSeparator />}
          {!isDeliveryTypeLocked && (
            <DeliveryMethod
              deliveryType={deliveryType}
              onDeliveryChanged={setDeliveryType}
              isProcessing={isProcessingPayment || isInitializing}
            />
          )}
          {!isDeliveryTypeLocked && <ContentSeparator />}
          {deliveryType === 'PickUp' && <LocalPickupInfo item={item} />}
          {deliveryType === 'Shipment' && <AddressSelection isProcessing={isProcessingPayment || isInitializing} />}
          {displayPaymentSelection && <ContentSeparator />}
          {displayPaymentSelection && <PaymentSelection isProcessing={isProcessingPayment || isInitializing} />}
        </MainColumn>
        <DetailsColumn>
          <OrderSummary
            deliveryType={deliveryType}
            purchase={purchase}
            checkoutCardStatus={checkoutCardStatus}
            checkoutTokenStatus={checkoutTokenStatus}
            isProcessing={isProcessingPayment || isProcessingPurchase || isInitializing}
            confirmCheckoutWithCard={confirmCheckoutWithCard}
            confirmCheckoutWithToken={confirmCheckoutWithToken}
          />
        </DetailsColumn>
      </ColumnsContainer>
    </CheckoutWrapper>
  );
};

export default ItemCheckoutContent;
